import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, RefreshCcw, ChevronRight } from "lucide-react";
import { formatRateChip } from "@/lib/rentalPricing";

const WINDOWS = [7, 14, 30, 60, 90];

export default function AdminRenewalsListPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [withinDays, setWithinDays] = useState(30);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/rentals/renewal-radar?within_days=${withinDays}&limit=200`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [withinDays]);
    useEffect(() => { load(); }, [load]);

    const sendRenewalInvite = async (rental) => {
        if (busyId) return;
        setBusyId(rental.id);
        try {
            const { data: contract } = await api.post(`/admin/customers/${rental.customer_id}/contracts`, {
                type: "rental_agreement",
                title: `Renewal — ${rental.equipment_type || "rental"}${rental.unit_number ? ` · ${rental.unit_number}` : ""}`,
                is_renewal: true,
            });
            const payload = {
                equipment_type: rental.equipment_type,
                unit_number: rental.unit_number,
                make: rental.make || null,
                model: rental.model || null,
                year: rental.year || null,
                start_date: rental.renewal_date,
                daily_rate: rental.daily_rate ?? undefined,
                monthly_rate: rental.monthly_rate ?? undefined,
                billing_period: rental.billing_period || undefined,
                minimum_term_months: rental.minimum_term_months || undefined,
            };
            Object.keys(payload).forEach((k) => { if (payload[k] === undefined || payload[k] === null) delete payload[k]; });
            await api.post(`/admin/contracts/${contract.id}/rental`, payload);
            try {
                const { data: sent } = await api.post(`/admin/contracts/${contract.id}/send-for-signature`);
                toast.success(`Renewal invite sent · ${sent.title}`);
            } catch (sendErr) {
                const detail = sendErr?.response?.data?.detail || "";
                if (/email/i.test(detail)) {
                    toast.warning("Draft created — add an email and resend");
                } else {
                    toast.error(formatApiError(sendErr));
                }
            }
            window.location.href = `/admin/contracts/${contract.id}`;
        } catch (err) { toast.error(formatApiError(err)); setBusyId(null); }
    };

    return (
        <DashboardShell>
            <div className="fade-up flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">Operations</p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
                        <Calendar size={26} className="text-slate-400" /> Renewal Radar
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">Active rentals with a minimum term ending soon — perfect upsell window.</p>
                </div>
                <div className="text-right">
                    <p className="text-3xl font-display font-bold text-slate-900" data-testid="renewals-page-total">{total}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Within {withinDays} days</p>
                </div>
            </div>

            <Card className="mt-6">
                <CardContent className="p-4 flex flex-wrap items-center gap-2">
                    <span className="text-xs uppercase tracking-wider text-slate-500 mr-2">Window</span>
                    {WINDOWS.map((d) => (
                        <button key={d} type="button"
                            onClick={() => setWithinDays(d)}
                            data-testid={`renewals-window-${d}`}
                            className={`h-8 px-3 rounded-md border text-xs font-medium transition-colors ${
                                withinDays === d ? "bg-blue-900 border-blue-900 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                            }`}>{d} days</button>
                    ))}
                </CardContent>
            </Card>

            <Card className="mt-4">
                <CardContent className="p-0">
                    {loading && <p className="text-sm text-slate-500 p-6" data-testid="renewals-loading">Loading…</p>}
                    {!loading && items.length === 0 && (
                        <div className="p-12 text-center" data-testid="renewals-empty">
                            <p className="font-display text-lg font-semibold text-slate-900">No renewals coming up</p>
                            <p className="mt-2 text-sm text-slate-600">Try a wider window — or come back when min-term rentals get closer to renewal.</p>
                        </div>
                    )}
                    {!loading && items.length > 0 && (
                        <table className="min-w-full text-sm">
                            <thead className="text-xs text-slate-500 uppercase border-b border-slate-100">
                                <tr>
                                    <th className="text-left py-3 px-4">Customer</th>
                                    <th className="text-left py-3 px-4">Equipment</th>
                                    <th className="text-left py-3 px-4">Rate</th>
                                    <th className="text-left py-3 px-4">Renewal date</th>
                                    <th className="text-left py-3 px-4">Days</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((r) => (
                                    <tr key={r.id} data-testid={`renewals-row-${r.id}`} className="hover:bg-slate-50/40">
                                        <td className="py-3 px-4">
                                            <Link to={`/admin/customers/${r.customer_id}`} className="text-blue-900 hover:underline font-medium">
                                                {r.customer_company_name || "—"}
                                            </Link>
                                            {r.customer_dba && <div className="text-xs text-slate-500">DBA {r.customer_dba}</div>}
                                        </td>
                                        <td className="py-3 px-4 capitalize text-slate-700">
                                            {(r.equipment_type || "").replace("_", " ") || "—"}
                                            {r.unit_number && <div className="text-[11px] text-slate-500 normal-case">#{r.unit_number}</div>}
                                        </td>
                                        <td className="py-3 px-4 text-slate-700">{formatRateChip(r) || "—"}</td>
                                        <td className="py-3 px-4 text-slate-700">{r.renewal_date}</td>
                                        <td className="py-3 px-4 font-semibold text-slate-900">{r.days_until_renewal}</td>
                                        <td className="py-3 px-4 text-right space-x-2">
                                            <Button size="sm" variant="outline" className="gap-1.5"
                                                disabled={busyId === r.id}
                                                onClick={() => sendRenewalInvite(r)}
                                                data-testid={`renewals-invite-${r.id}`}>
                                                <RefreshCcw size={12} /> {busyId === r.id ? "…" : "Send renewal invite"}
                                            </Button>
                                            <Button size="sm" variant="ghost" asChild>
                                                <Link to={`/admin/rentals/${r.id}`} data-testid={`renewals-open-${r.id}`}>
                                                    Open <ChevronRight size={12} />
                                                </Link>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </DashboardShell>
    );
}
